/* ============================================================
   BNE STUDIO — Distribution (Midnight Blueprint)
   Recreated from the design handoff:
   "BNE Studio - Distribution.html". Renders inside NightLayout
   (shared nav/footer + motion engine + scoped design system).
   The page lives at /distribute.
   ============================================================ */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";

// arrow used by the primary CTAs
const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DASHBOARD_URL = "https://dashboard.bne.studio";
const SYNC_URL = "https://one.bne.studio";
const VISION_URL = "https://vision.bne.studio";
const SMART_LINKS_URL = "https://bne.to";
const LIVE_URL = "https://bne.live";

export const DistributePage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "BNE Distribution — Release your music worldwide";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout description="Deliver music, manage releases, and keep your catalog moving from one distribution dashboard. Built for artists and labels alike." active="dist">
      {/* HERO */}
      <header className="phero" style={{ "--c": "var(--p-dist)" }}>
        <div className="wrap">
          <div className="phero-grid">
            <div className="reveal">
              <div className="ico-badge">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="eyebrow" style={{ marginBottom: "16px" }}>Release</span>
              <h1 className="grad-text">Go global in one click.</h1>
              <p className="lead">Send your song to every store on the planet and watch it go live everywhere at once.</p>
              <div className="ctas">
                <a className="btn btn-primary btn-lg btn-arrow" href={DASHBOARD_URL}>
                  Start Distributing <Arrow />
                </a>
                <span className="domain">dashboard.bne.studio</span>
              </div>
            </div>

            <div className="reveal" data-d="1">
              <div className="big-mock">
                <div className="bm-bar">
                  <i />
                  <i />
                  <i />
                  <span className="t">dashboard.bne.studio / catalog</span>
                  <span className="pill">Catalog</span>
                </div>
                <div className="bm-body">
                  <div className="statgrid">
                    <div className="s">
                      <div className="k">Live</div>
                      <div className="v" data-count="48">0</div>
                    </div>
                    <div className="s">
                      <div className="k">In review</div>
                      <div className="v" data-count="6">0</div>
                    </div>
                    <div className="s">
                      <div className="k">Streams 30d</div>
                      <div className="v">
                        <span data-count="1.4" data-dec="1">0</span>M
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <span className="art" style={{ background: "linear-gradient(135deg,#2f48ff,#8b93ff)" }} />
                    <span className="m">
                      <b>Neon Hours — Single</b>
                      <span>2 platforms pending</span>
                    </span>
                    <span className="chip-status st-live">Live</span>
                  </div>
                  <div className="row">
                    <span className="art" style={{ background: "linear-gradient(135deg,#ff7a5c,#a877ff)" }} />
                    <span className="m">
                      <b>Afterglow — EP</b>
                      <span>master approved</span>
                    </span>
                    <span className="chip-status st-q">In review</span>
                  </div>
                  <div className="row">
                    <span className="art" style={{ background: "linear-gradient(135deg,#33c6a6,#6f86ff)" }} />
                    <span className="m">
                      <b>Lowlight — Album</b>
                      <span>scheduled Mar 14</span>
                    </span>
                    <span className="chip-status st-q">Queued</span>
                  </div>
                  <div className="row">
                    <span className="art" style={{ background: "linear-gradient(135deg,#6f86ff,#a877ff)" }} />
                    <span className="m">
                      <b>Drift — Single</b>
                      <span>live on 14 stores</span>
                    </span>
                    <span className="chip-status st-live">Live</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* OUTCOMES */}
      <section className="section tight" style={{ "--c": "var(--p-dist)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">What it helps you do</span>
            <h2>Everything a release needs, in one dashboard.</h2>
          </div>
          <div className="outcomes">
            <div className="outcome reveal" data-d="1">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Deliver everywhere</h4>
              <p>Send singles, EPs, and albums to every major streaming platform from one upload.</p>
            </div>
            <div className="outcome reveal" data-d="2">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Manage the catalog</h4>
              <p>Keep metadata, splits, artwork, and release dates organized and consistent across your roster.</p>
            </div>
            <div className="outcome reveal" data-d="3">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 3v18h18M7 14l3-3 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Track the rollout</h4>
              <p>Watch delivery statuses, store links, and early performance as the release goes live.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="section" style={{ "--c": "var(--p-dist)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>From upload to live in four steps.</h2>
          </div>
          <div className="steps">
            <div className="sline reveal" />
            <div className="step reveal" data-d="1">
              <div className="k">1</div>
              <h4>Upload the master</h4>
              <p>Drop in audio and artwork, or pull an approved master straight from Sync.</p>
            </div>
            <div className="step reveal" data-d="2">
              <div className="k">2</div>
              <h4>Add the details</h4>
              <p>Set metadata, splits, and a release date — with smart checks before you submit.</p>
            </div>
            <div className="step reveal" data-d="3">
              <div className="k">3</div>
              <h4>Choose stores</h4>
              <p>Deliver to every major platform, or pick exactly where the release should land.</p>
            </div>
            <div className="step reveal" data-d="4">
              <div className="k">4</div>
              <h4>Go live &amp; track</h4>
              <p>Confirm delivery, grab store links, and watch the first numbers roll in.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="section tight" style={{ "--c": "var(--p-dist)" }}>
        <div className="wrap">
          <div className="proof">
            <div className="pc reveal" data-d="1">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8h16M4 8a2 2 0 012-2h12a2 2 0 012 2M4 8v10a2 2 0 002 2h12a2 2 0 002-2V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Delivery status
              </div>
              <h4>See every store, in real time.</h4>
              <p>Each platform reports back as the release lands — so you always know what's live and what's still in flight.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span className="m">
                    <b>Spotify</b>
                    <span>delivered · 2h ago</span>
                  </span>
                  <span className="chip-status st-live">Live</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Apple Music</b>
                    <span>delivered · 2h ago</span>
                  </span>
                  <span className="chip-status st-live">Live</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Amazon Music</b>
                    <span>processing</span>
                  </span>
                  <span className="chip-status st-q">Pending</span>
                </div>
              </div>
            </div>

            <div className="pc reveal" data-d="2">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2zM4 10h16M9 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Catalog &amp; metadata
              </div>
              <h4>One source of truth for the roster.</h4>
              <p>Splits, credits, ISRCs, and release dates stay consistent across every artist and every release.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span className="art" style={{ background: "linear-gradient(135deg,#2f48ff,#8b93ff)" }} />
                  <span className="m">
                    <b>Neon Hours</b>
                    <span>ISRC · UK-BNE-26-001</span>
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--p-dist)" }}>60 / 40</span>
                </div>
                <div className="row">
                  <span className="art" style={{ background: "linear-gradient(135deg,#ff7a5c,#a877ff)" }} />
                  <span className="m">
                    <b>Afterglow</b>
                    <span>ISRC · UK-BNE-26-014</span>
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--p-dist)" }}>50 / 50</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="section" style={{ "--c": "var(--p-dist)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Connected to the rest of BNE</span>
            <h2>Distribution doesn't work alone.</h2>
          </div>
          <div className="eco-grid">
            <a className="eco reveal" data-d="1" style={{ "--c": "var(--p-sync)" }} href={SYNC_URL}>
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6a2 2 0 012-2h4l2 2.5h6a2 2 0 012 2V18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Sync</b>
              <p>Send approved masters straight into a release.</p>
            </a>
            <a className="eco reveal" data-d="2" style={{ "--c": "var(--p-vision)" }} href={VISION_URL}>
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3ZM3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Vision</b>
              <p>Create rollout visuals from the same track.</p>
            </a>
            <a className="eco reveal" data-d="3" style={{ "--c": "var(--p-links)" }} href={SMART_LINKS_URL}>
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 15l6-6M10 6l1-1a4 4 0 016 6l-1 1M14 18l-1 1a4 4 0 01-6-6l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Smart Links</b>
              <p>Promote the release with one campaign link.</p>
            </a>
            <a className="eco reveal" data-d="4" style={{ "--c": "var(--p-live)" }} href={LIVE_URL}>
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H6a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Live</b>
              <p>Sell tickets around the release moment.</p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section tight">
        <div className="wrap">
          <div className="cta-band reveal-scale">
            <div className="glow" />
            <span className="eyebrow center" style={{ justifyContent: "center", marginBottom: "20px" }}>
              Release · Distribution
            </span>
            <h2 className="grad-text">Get the next release out.</h2>
            <p>Upload a master, set the date, and deliver everywhere from one dashboard.</p>
            <div className="row" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a className="btn btn-primary btn-lg btn-arrow" href={DASHBOARD_URL}>
                Start Distributing <Arrow />
              </a>
              <Link className="btn btn-ghost btn-lg" to="/home">
                Explore the Ecosystem
              </Link>
            </div>
          </div>
        </div>
      </section>
    </NightLayout>
  );
};

export default DistributePage;
